import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import { io, Socket } from "socket.io-client";
import { STORAGE_KEYS } from "../constants";
import { AppConfig } from "../config";

type SocketCtx = {
  socket: Socket | null;
  connected: boolean;
};

const Ctx = createContext<SocketCtx>({ socket: null, connected: false });

function getToken() {
  // same JWT you send to REST
  return localStorage.getItem(STORAGE_KEYS.TOKEN);
}

type Props = {
  apiBaseUrl?: string;           // e.g. https://api.yourdomain.com
  socketPath?: string;          // default: /socket.io
  children: React.ReactNode;
};

export function SocketProvider({ apiBaseUrl = AppConfig.getBaseURL(), socketPath = "/socket.io", children }: Props) {
  const [connected, setConnected] = useState(false);
  const socketRef = useRef<Socket | null>(null);

  useEffect(() => {
    const s = io(apiBaseUrl, {
      path: socketPath,
      transports: ["websocket"],     // prefer WS in prod
      withCredentials: true,
      auth: { token: getToken() },
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      timeout: 20000,
    });

    // keep token fresh for every reconnect attempt
    s.on("reconnect_attempt", () => {
      s.auth = { token: getToken() };
    });

    s.on("connect", () => setConnected(true));
    s.on("disconnect", () => setConnected(false));
    s.on("connect_error", (err) => {
      // helpful in dev
      console.error("[socket] connect_error:", err.message);
    });

    socketRef.current = s;

    return () => {
      s.removeAllListeners();
      s.close();
      socketRef.current = null;
    };
  }, [apiBaseUrl, socketPath]);

  const value = useMemo(() => ({ socket: socketRef.current, connected }), [connected]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useSocket() {
  return useContext(Ctx);
}
